import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Button, DrawerStepper } from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import {
  useValidationErrors,
  onSuccessMessage,
  onErrorMessage,
  deepCopy,
} from '@/helpers'

import {
  useUpdateProfile,
  QUERIES_ADMIN,
  useCreateProfile,
  useProfileDetail,
  useUserList,
} from '@/services'

import { ProfileProps } from '@/types'

import { ProfileForm, schemaProfile } from '../profile-form'
import { BuyerInformationDrawerProps } from './buyer-information-drawer.type'

export const BuyerInformationDrawer = ({
  isOpen,
  onHandleOpen,
  profileUuid,
}: BuyerInformationDrawerProps) => {
  const { t } = useTranslation('screenProfileShow')

  const isNewRegister = !profileUuid

  const queryClient = useQueryClient()

  const [data, setData] = useState<Partial<ProfileProps>>({})

  const {
    isValid: isValidProfile,
    errors: profileErrors,
    onHandleListenErrors: onHandleListenErrorsProfile,
  } = useValidationErrors(schemaProfile(t), data)

  const { data: dataProfile } = useProfileDetail(profileUuid || '')

  const { mutateAsync: mutateAsyncUpdateProfile } = useUpdateProfile()
  const { mutateAsync: mutateAsyncCreateProfile } = useCreateProfile()

  const onSubmitCompleted = async () => {
    let response: {
      success: boolean
      message: string
      data?: ProfileProps
    } = {
      success: false,
      message: '',
      data: {} as ProfileProps,
    }

    const payload = {
      ...data,
      churnedAt: data?.churnedAt ? data?.churnedAt : null,
      addressProof: undefined,
    }

    if (isNewRegister) {
      response = await mutateAsyncCreateProfile(payload as ProfileProps)
    } else {
      response = await mutateAsyncUpdateProfile(payload as ProfileProps)
    }

    if (response.success) {
      if (isNewRegister) {
        onSuccessMessage(
          t('profileFormSuccessCreateTitle'),
          t('profileFormSuccessCreateMessage'),
        )

        onHandleOpen(false)
      } else {
        onSuccessMessage(
          t('profileFormSuccessUpdateTitle'),
          t('profileFormSuccessUpdateMessage'),
        )
      }

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.PROFILE.module],
      })

      setData({})
      onHandleOpen(false)
    } else {
      if (isNewRegister) {
        onErrorMessage(
          t('profileFormErrorCreateTitle'),
          t('profileFormErrorCreateMessage'),
        )
      } else {
        onErrorMessage(
          t('profileFormErrorUpdateTitle'),
          t('profileFormErrorUpdateMessage'),
        )
      }
    }
  }

  const onSubmitProfileForm = () => {
    if (isValidProfile) {
      onHandleListenErrorsProfile(false)

      onSubmitCompleted()
    } else {
      onHandleListenErrorsProfile(true)
    }
  }

  useEffect(() => {
    if (isOpen && !isNewRegister && dataProfile?.data) {
      const deepDataCopy = deepCopy(dataProfile.data)

      if (deepDataCopy && deepDataCopy.taxSituation) {
        delete (deepDataCopy as { taxSituation?: string }).taxSituation
      }

      setData({
        ...deepDataCopy,
        keyAccountManagerId: dataProfile?.data?.keyAccountManager?.id
          ? `${dataProfile?.data?.keyAccountManager?.id}`
          : undefined,
        customerLevelSlug: dataProfile?.data?.customerLevel?.slug
          ? dataProfile?.data?.customerLevel?.slug
          : undefined,
        taxStatus: dataProfile?.data?.taxStatus || 'pending',
      })
    }

    if (!isOpen || isNewRegister) {
      setData({})
    }
  }, [isOpen, dataProfile?.data, isNewRegister])

  const { data: dataUsers } = useUserList({
    params: {
      limit: 100,
      page: 1,
      isStaff: 'true',
    },
  })

  return (
    <DrawerStepper
      translations={{
        optional: t('profileFormOptional'),
      }}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      title={t('buyerGeneralInformationDrawerTitle')}
      initialStep="root"
      steps={{
        root: {
          isOptional: false,
          backNavigate: 'root',
          component: (
            <ProfileForm
              errors={profileErrors as Record<string, string>}
              values={data || {}}
              onChangeValues={setData}
              usersData={dataUsers?.data?.results || []}
            />
          ),
          footer: () => (
            <>
              <Button variant="secondary" onClick={() => onHandleOpen(false)}>
                {t('profileFormNavigateButtonBack')}
              </Button>
              <Button onClick={() => onSubmitProfileForm()}>
                {t('profileFormNavigateButtonFinish')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
